export interface DonationPayment {
  id: number;
  razorpay_order_id?: string;
  razorpay_payment_id?: string | null;
  razorpay_signature?: string | null;
  amount: string;
  currency?: string;
  status?: string;
  created_at?: string;
  payment_method?: string | null;
  payment_method_details?: unknown;
  receipt_no?: string | null;
}

export interface DonationDetailRecord {
  id: number;
  scheme_id?: number | null;
  scheme?: number;
  scheme_title?: string | null;
  scheme_institution_name?: string | null;
  name?: string;
  email?: string;
  phone?: string;
  donor_name?: string;
  donor_mobile?: string;
  organization?: string | null;
  donation_type?: string;
  category?: string;
  category_name?: string;
  amount?: string | number;
  pan_number?: string | null;
  aadhaar_number?: string | null;
  message?: string | null;
  created_at?: string;
  donation_date?: string;
  receipt_no?: string | null;
  receipt_number?: string;
  mode?: string;
  payment_mode?: string;
  payment_status?: string;
  transaction_id?: string | null;
  purpose?: string | null;
  purpose_of_donation?: string | null;
  remarks?: string | null;
  payments?: DonationPayment[];
  address_line1?: string | null;
  address_line2?: string | null;
  state?: string | number | null;
  state_id?: number | null;
  state_name?: string | null;
  district?: string | number | null;
  district_id?: number | null;
  district_name?: string | null;
  city?: string | number | null;
  city_id?: number | null;
  city_name?: string | null;
  pincode?: string | null;
  by_cheque_no?: string | null;
  updated_at?: string;
}

export function getDonationReceiptNo(
  record: Pick<
    DonationDetailRecord,
    "receipt_no" | "receipt_number" | "payments"
  >
): string {
  const paymentReceipt = record.payments?.[0]?.receipt_no;
  return String(
    record.receipt_no ?? record.receipt_number ?? paymentReceipt ?? "—"
  );
}

export function getDonationDonorName(
  record: Pick<DonationDetailRecord, "name" | "donor_name">
): string {
  const name = (record.name ?? record.donor_name)?.trim();
  return name ?? "Anonymous";
}

export function getDonationEmail(
  record: Pick<DonationDetailRecord, "email">
): string {
  return record.email?.trim() ?? "—";
}

export function getDonationPhone(
  record: Pick<DonationDetailRecord, "phone" | "donor_mobile">
): string {
  return record.phone ?? record.donor_mobile ?? "—";
}

export function getDonationTypeLabel(
  record: Pick<
    DonationDetailRecord,
    "donation_type" | "category" | "category_name"
  >
): string {
  return String(
    record.donation_type ?? record.category_name ?? record.category ?? "—"
  );
}

const PAYMENT_MODE_LABELS: Record<string, string> = {
  neft: "NEFT",
  rtgs: "RTGS",
  imps: "IMPS",
  upi: "UPI",
  cheque: "Cheque",
  cash: "Cash",
  wallet: "Wallet",
  card: "Card",
  netbanking: "Netbanking",
  online: "Online",
  offline: "Offline",
};

export function getDonationModeLabel(
  record: Pick<DonationDetailRecord, "mode" | "payment_mode" | "payments">
): string {
  const mode = record.mode?.trim();
  if (mode) {
    const key = mode.toLowerCase();
    return (
      PAYMENT_MODE_LABELS[key] ?? mode.charAt(0).toUpperCase() + mode.slice(1)
    );
  }
  return "—";
}

export function getDonationCampaignLabel(
  record: Pick<
    DonationDetailRecord,
    "scheme_title" | "purpose_of_donation" | "purpose"
  >
): string {
  const campaign = record.scheme_title?.trim();
  if (campaign) {
    return campaign;
  }
  const purpose =
    record.purpose_of_donation?.trim() ?? record.purpose?.trim() ?? "";
  return purpose || "—";
}

export function getDonationAmount(
  record: Pick<DonationDetailRecord, "amount">
): number {
  return Number(record.amount ?? 0);
}

export function getPaymentStatus(record: DonationDetailRecord): string {
  const payments = record.payments;
  if (payments?.length) {
    return payments[0].status ?? record.payment_status ?? "—";
  }
  return record.payment_status ?? "—";
}

export function formatPaymentStatusLabel(status?: string | null): string {
  if (!status || status === "—") {
    return "—";
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export function paymentStatusBadgeClass(status?: string | null): string {
  switch ((status ?? "success").toLowerCase()) {
    case "success":
    case "captured":
      return "bg-green-600 text-white hover:bg-green-600";
    case "created":
    case "pending":
      return "bg-secondary text-secondary-foreground hover:bg-secondary";
    case "failed":
      return "bg-destructive text-destructive-foreground hover:bg-destructive";
    default:
      return "bg-muted text-muted-foreground hover:bg-muted";
  }
}

export function formatDonationDate(iso?: string | null): string {
  if (!iso) {
    return "—";
  }
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function buildDonationAddress(record: DonationDetailRecord): string {
  const parts = [
    record.address_line1,
    record.address_line2,
    record.city_name ?? record.city,
    record.district_name ?? record.district,
    record.state_name ?? record.state,
    record.pincode,
  ]
    .map(part =>
      part !== null && part !== undefined ? String(part).trim() : ""
    )
    .filter(Boolean);
  return parts.join(", ");
}
